import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { PageHero } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { fitDescription, fitTitle } from "@/lib/seo-text";
import { FIRM } from "@/lib/firm";
import { getPost, isDraftPost } from "@/lib/blog";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Post not found" }, { name: "robots", content: "noindex" }] };
    }
    const p = loaderData.post;
    const url = `https://coastsidelaw.com.au/blog/${params.slug}`;
    return {
      meta: [
        { title: fitTitle(`${p.metaTitle} | Coastside Law`) },
        { name: "description", content: fitDescription(p.description) },
        { property: "og:title", content: p.title },
        { property: "og:description", content: fitDescription(p.description) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: p.image?.src.startsWith("https://") ? "summary_large_image" : "summary" },
        ...(p.image?.src.startsWith("https://") ? [
          { property: "og:image", content: p.image.src },
          { name: "twitter:image", content: p.image.src },
          { property: "og:image:alt", content: p.image.alt },
        ] : []),
        ...(isDraftPost(p.slug) ? [{ name: "robots", content: "noindex" }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: p.title,
            description: p.description,
            datePublished: p.date,
            dateModified: p.date,
            mainEntityOfPage: url,
            ...(p.image?.src.startsWith("https://") ? { image: p.image.src } : {}),
            author: p.author ? { "@type": "Person", name: p.author, url: "https://coastsidelaw.com.au/our-people" } : { "@type": "Organization", name: FIRM.name, url: "https://coastsidelaw.com.au" },
            publisher: { "@type": "Organization", name: FIRM.name },
          }),
        },
      ],
    };
  },
  component: BlogPostPage,
});

function BlogPostPage() {
  const { post } = Route.useLoaderData();
  return (
    <>
      <PageHero eyebrow={`${post.category} · ${post.area}`} title={post.title} lead={post.intro} {...(!post.editorial ? { cta: post.cta } : {})} {...(post.image ? { image: post.image } : {})}>
        {post.author && <p className="mt-6 text-sm text-primary-foreground">By {post.author} · Coastside Law · <time dateTime={post.date}>{new Date(`${post.date}T12:00:00Z`).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric", timeZone: "Australia/Brisbane" })}</time></p>}
      </PageHero>
      <article className="mx-auto max-w-3xl px-5 py-14">
        {post.sections.map((s) => (
          <section key={s.heading} className="mt-10 first:mt-0">
            <h2 className="font-serif text-2xl text-foreground">{s.heading}</h2>
            {s.paragraphs.map((para) => (
              <p key={para} className="measure mt-4 leading-7 text-muted-foreground">
                {para}
              </p>
            ))}
            {s.bullets && (
              <ul className="mt-4 list-disc space-y-2 pl-6 text-muted-foreground">
                {s.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {post.sources && (
          <section className="mt-12 border-t border-border pt-8">
            <h2 className="font-serif text-xl text-foreground">{post.editorial ? "Cases and legislation" : "Sources and local information"}</h2>
            <ul className="mt-4 space-y-3">
              {post.sources.map((source) => (
                <li key={source.url}>
                  <a href={source.url} className="text-sm font-semibold leading-6 text-primary underline underline-offset-4">
                    {source.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <aside className="mt-12 rounded-sm border border-border bg-secondary/40 p-6">
          <h2 className="font-serif text-xl text-foreground">Read more</h2>
          <ul className="mt-3 space-y-2">
            {post.links.map((l) => (
              <li key={l.label}>
                <Link to={l.to} params={l.params as never} className="text-sm font-semibold text-primary underline-offset-4 hover:underline">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </aside>

        <p className="mt-8 text-xs leading-6 text-muted-foreground">
          {post.disclaimer ?? "General information about Queensland law, not legal advice for your situation."}
        </p>
      </article>

      {!post.editorial && <section className="mx-auto max-w-3xl scroll-mt-24 px-5 pb-16">
        <EnquiryForm heading={post.cta} />
      </section>}

      <div className="mx-auto max-w-3xl px-5 pb-16">
        <Link to="/blog" className="text-sm font-semibold text-primary hover:underline">
          ← All blog posts
        </Link>
      </div>
    </>
  );
}
